import React from 'react';
import { Stack, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSummaryFetch, WidgetLoadError } from './useSummaryFetch';
import { UserType } from '../../../interfaces/userType.enum';
import { DashboardService } from '../../../services/dashboard.service';
import { PayrollSummary, PayrollPeriodTotal, DepartmentAmount, MonthlyHours } from '../../../interfaces/dashboard.interface';
import { WidgetDef, WidgetProps } from './types';
import StatTile from '../charts/StatTile';
import Columns from '../charts/Columns';
import HBars from '../charts/HBars';
import { fmtMoney, CURRENCY_SYMBOL } from './format';
import { compactMoney, formatPeriodLabel } from '../charts/axisPadding';

const BUSINESS_ROLES = [UserType.InstitutionAdmin, UserType.InstitutionManager, UserType.Accountant];

const loadPayroll = (institutionId: string): Promise<PayrollSummary> =>
    DashboardService.getPayrollSummary(institutionId);

const PayrollWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const { data: d, failed } = useSummaryFetch(loadPayroll, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.lastRunPeriod == null) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{t('widgets.payroll.noRun')}</Typography>;
    }
    return (
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <StatTile label={t('widgets.payroll.lastRunNet')} unit={CURRENCY_SYMBOL} value={fmtMoney(d.lastRunTotal)} sub={formatPeriodLabel(d.lastRunPeriod)} />
            <StatTile label={t('widgets.payroll.employeesPaid')} value={d.headcount} />
        </Stack>
    );
};

const loadPayrollTrend = (institutionId: string): Promise<PayrollPeriodTotal[]> =>
    DashboardService.getPayrollTrend(institutionId);

const PayrollTrendWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const { data: d, failed } = useSummaryFetch(loadPayrollTrend, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{t('widgets.payroll.noRuns')}</Typography>;
    }
    return <Columns data={d.map(p => ({ label: p.period, value: p.netTotal }))} yFmt={fmtMoney} tickFmt={compactMoney} />;
};

const loadByDepartment = (institutionId: string): Promise<DepartmentAmount[]> =>
    DashboardService.getPayrollByDepartment(institutionId);

const PayrollByDepartmentWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const { data: d, failed } = useSummaryFetch(loadByDepartment, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{t('widgets.payroll.noRunDepartments')}</Typography>;
    }
    return (
        <HBars
            data={d.map(g => ({
                label: g.department,
                value: g.amount,
                tooltip: [
                    { label: t('widgets.payroll.net'), value: fmtMoney(g.amount) },
                    { label: t('widgets.payroll.headcount'), value: String(g.headcount) },
                ],
            }))}
            vFmt={fmtMoney}
        />
    );
};

const loadOvertime = (institutionId: string): Promise<MonthlyHours[]> =>
    DashboardService.getPayrollOvertime(institutionId);

const PayrollOvertimeWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const { data: d, failed } = useSummaryFetch(loadOvertime, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{t('widgets.payroll.noOvertime')}</Typography>;
    }
    // Hours, not money — no yFmt.
    return <Columns data={d.map(m => ({ label: m.month, value: m.hours }))} />;
};

export const payrollWidgets: WidgetDef[] = [
    { id: 'payroll.summary', module: 'payroll', titleKey: 'widgets.titles.payrollSummary', subtitleKey: 'widgets.subtitles.payrollSummary', roles: BUSINESS_ROLES, size: 'md', component: PayrollWidget },
    { id: 'payroll.trend', module: 'payroll', titleKey: 'widgets.titles.payrollTrend', subtitleKey: 'widgets.subtitles.payrollTrend', roles: BUSINESS_ROLES, size: 'lg', component: PayrollTrendWidget },
    { id: 'payroll.byDepartment', module: 'payroll', titleKey: 'widgets.titles.payrollByDepartment', subtitleKey: 'widgets.subtitles.payrollByDepartment', roles: BUSINESS_ROLES, size: 'md', component: PayrollByDepartmentWidget },
    { id: 'payroll.overtime', module: 'payroll', titleKey: 'widgets.titles.payrollOvertime', subtitleKey: 'widgets.subtitles.payrollOvertime', roles: BUSINESS_ROLES, size: 'md', component: PayrollOvertimeWidget },
];
